// Select - Choices.js wrapper for .js-select elements: sk/cz texts, Martinus
// class names and the data-attribute variants (data-select-store,
// data-select-image, data-select-wide-image, data-autocomplete, ...).
// Behavioral port of v1 modules/Select.js.
//
// v1's static Select.setLang() (fed from window.myApp.selectLanguage) is
// replaced by the `language` config option; the default is read from
// <html lang> at init time.

import Choices from 'choices.js';

export type SelectLanguage = 'sk' | 'cz';

// Choices' generated typings don't cover the template-callback runtime shapes
// we rely on (v1 relied on them too), so keep these local aliases loose.
type ChoicesOptions = {
  classNames: Record<string, string | string[]>;
  [key: string]: unknown;
};
type TemplateData = {
  id: number;
  value: string;
  label: string;
  disabled?: boolean;
  placeholder?: boolean;
  customProperties?: Record<string, string>;
};
type TemplateFn = (html: string) => HTMLElement;

/**
 * Choices class names are `string | string[]` - it feeds them to
 * classList.add(), our templates interpolate them into a class attribute, so
 * they have to be flattened to a space separated string here.
 */
const classAttr = (value: string | string[] | undefined): string =>
  (Array.isArray(value) ? value.join(' ') : (value ?? ''));

export type SelectConfig = Record<string, unknown> & {
  language?: SelectLanguage;
};

const texts = {
  sk: {
    loadingText: 'Nahrávam...',
    noResultsText: 'Žiadne výsledky',
    noChoicesText: 'Na výber nie sú žiadne možnosti',
    itemSelectText: 'Kliknutím vyberte',
    addItemText: (value: string) => `Stlačením ENTER pridajte <b>"${value}"</b>`,
    maxItemText: (maxItemCount: number) => `Iba ${maxItemCount} hodnôt môže byť pridaných.`,
  },
  cz: {
    loadingText: 'Nahrávám...',
    noResultsText: 'Žádné výsledky',
    noChoicesText: 'Na výběr nejsou žádné možnosti',
    itemSelectText: 'Kliknutím vyberte',
    addItemText: (value: string) => `Stlačením ENTER přidejte <b>"${value}"</b>`,
    maxItemText: (maxItemCount: number) => `Jenom ${maxItemCount} hodnot může být přidaných.`,
  },
} satisfies Record<SelectLanguage, Record<string, unknown>>;

const defaultConfig = {
  searchEnabled: false,
  searchChoices: false,
  shouldSort: false,
  itemSelectText: 'Press to select',
  allowHTML: true,
  classNames: {
    containerOuter: 'choices',
    containerInner: 'choices__inner',
    input: 'choices__input',
    inputCloned: 'choices__input--cloned',
    list: 'choices__list',
    listItems: 'choices__list--multiple',
    listSingle: 'choices__list--single',
    listDropdown: 'choices__list--dropdown',
    item: 'choices__item',
    itemSelectable: 'choices__item--selectable',
    itemDisabled: 'choices__item--disabled',
    itemChoice: 'choices__item--choice',
    group: 'choices__group',
    groupHeading: 'choices__heading',
    button: 'choices__button',
    activeState: 'is-active',
    focusState: 'is-focused',
    openState: 'is-open',
    disabledState: 'is-disabled',
    highlightedState: 'is-highlighted',
    flippedState: 'is-flipped',
    loadingState: 'is-loading',
  },
};

/** Resolve the default language from <html lang>. */
const detectLanguage = (): SelectLanguage => {
  const lang = document.documentElement.lang.toLowerCase();
  return lang.startsWith('cs') || lang.startsWith('cz') ? 'cz' : 'sk';
};

const STORE_ICONS = new Map([
  ['martinus', { icon: 'fak fa-martinus', color: 'text-color-primary' }],
  ['partner', { icon: 'far fa-map-marker-alt', color: 'text-color-yellow' }],
]);

const storeIcon = (type?: string): string => {
  const iconConfig = (type && STORE_ICONS.get(type)) || null;
  if (!iconConfig) return '';

  return `<i class="${iconConfig.icon} ${iconConfig.color} fa-fw mr-tiny flex-shrink-0"></i>`;
};

const storeStatus = (
  customProperties: Record<string, string>,
  placeholder?: boolean,
): string => {
  if (placeholder) {
    return '';
  }

  const { status, statusText = '' } = customProperties;
  const statusClass = status ? `status status--${status} text-color-${status}` : '';

  return `<div class="${statusClass} text-right text-small">${statusText}</div>`;
};

const storeListTemplate = ({ label, customProperties = {}, placeholder }: TemplateData) => {
  const icon = storeIcon(customProperties.type);

  const statusText = storeStatus(customProperties, placeholder);

  return `
  <div style="max-width: 100%;" class="d-flex align-items-center text-medium justify-content-between">
    <div class="d-flex align-items-center text-color-grey-dark text-left" style="max-width: 66%;">${icon}${label}</div>
    ${statusText}
  </div>
  `;
};

const storeListChoice = (
  { customProperties = {}, disabled, label, id, value, placeholder }: TemplateData,
  config: ChoicesOptions,
) => {
  const statusText = storeStatus(customProperties, placeholder);
  const icon = storeIcon(customProperties.type);

  return `
  <div style="max-width: 100%;" class="align-items-center justify-content-between ${classAttr(config.classNames.item)} ${classAttr(config.classNames.itemChoice)} ${classAttr(disabled ? config.classNames.itemDisabled : config.classNames.itemSelectable)}" data-select-text="${config.itemSelectText}" data-choice ${disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${id}" data-value="${value}">
    <div class="d-flex align-items-center text-color-black text-left text-regular" style="max-width: 66%;">${icon}${label}</div>
    ${statusText}
  </div>
  `;
};

const storeListChoices = (config: ChoicesOptions) => `
  <div class="${classAttr(config.classNames.list)}" dir="ltr" role="listbox" style="min-width:270px; max-height: none;">
  </div>
`;

const imageListTemplate = ({ label, customProperties }: TemplateData) => `
  <div class="d-flex align-items-center text-medium mb-none">
      ${customProperties && customProperties.image ? `<div class="img--rounded mr-small" style="width: 40px; height: 27px; background: url(${customProperties.image}) no-repeat center; background-size: cover"></div>` : ''}
      <div class="flex-1 mr-small">
        <span>${label}</span>
      </div>
      ${customProperties && customProperties.price ? `<span class="text-vam text-color-grey">${customProperties.price}</span>` : ''}
    </div>`;

const imageListChoice = (
  { customProperties, disabled, label, id, value }: TemplateData,
  config: ChoicesOptions,
) => `
  <div class="text-left ${classAttr(config.classNames.item)} ${classAttr(config.classNames.itemChoice)} ${classAttr(disabled ? config.classNames.itemDisabled : config.classNames.itemSelectable)}" data-select-text="${config.itemSelectText}" data-choice ${disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${id}" data-value="${value}">
    <div class="d-flex align-items-center mb-none">
      ${customProperties && customProperties.image ? `<div class="img--rounded mr-small" style="width: 80px; height: 80px; background: url(${customProperties.image}) no-repeat center; background-size: cover"></div>` : ''}
      <div class="flex-1">
        <span>${label}</span><br>
        ${customProperties && customProperties.price ? `<span class="text-vam text-color-grey">${customProperties.price}</span>` : ''}
      </div>
    </div>
  </div>
  `;

const wideImageListTemplate = ({ label }: TemplateData, width: string) => `
  <div class="mb-none" style="width:${width}">
    <div class="mb-none">
      <span>${label}</span>
    </div>
  </div>`;

const wideImageListChoice = (
  { customProperties, disabled, label, id, value }: TemplateData,
  config: ChoicesOptions,
) => `
  <div class="text-left ${classAttr(config.classNames.item)} ${classAttr(config.classNames.itemChoice)} ${classAttr(disabled ? config.classNames.itemDisabled : config.classNames.itemSelectable)}" data-select-text="${config.itemSelectText}" data-choice ${disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${id}" data-value="${value}">
    <div class="mb-none">
      <div class="mb-none">
        <span>${label}</span>
      </div>
      ${customProperties && customProperties.image ? `<div class="mb-none"><img class="img img--rounded" src="${customProperties.image}"></div>` : ''}
    </div>
  </div>
  `;

const choiceListTemplate = (width: string, config: ChoicesOptions) => `
  <div class="${classAttr(config.classNames.list)}" dir="ltr" role="listbox" style="width:${width}"></div>
`;

function getTemplates(
  template: TemplateFn,
  select: HTMLSelectElement,
  config: ChoicesOptions,
): Record<string, unknown> {
  let templates: Record<string, unknown> = {};

  if (select.hasAttribute('data-select-store')) {
    templates = {
      ...templates,
      item: (_classNames: unknown, data: TemplateData) =>
        template(storeListTemplate(data)),
      choice: (_classNames: unknown, data: TemplateData) =>
        template(storeListChoice(data, config)),
      choiceList: () => template(storeListChoices(config)),
    };
  }

  if (select.hasAttribute('data-select-image')) {
    templates = {
      ...templates,
      item: (_classNames: unknown, data: TemplateData) =>
        template(imageListTemplate(data)),
      choice: (_classNames: unknown, data: TemplateData) =>
        template(imageListChoice(data, config)),
    };
  }

  if (select.hasAttribute('data-select-wide-image')) {
    templates = {
      ...templates,
      item: (_classNames: unknown, data: TemplateData) =>
        template(
          wideImageListTemplate(data, select.getAttribute('data-select-width') ?? 'none'),
        ),
      choice: (_classNames: unknown, data: TemplateData) =>
        template(wideImageListChoice(data, config)),
    };
  }

  if (select.hasAttribute('data-select-dropdown-width')) {
    templates = {
      ...templates,
      choiceList: () =>
        template(
          choiceListTemplate(
            select.getAttribute('data-select-dropdown-width') ?? 'none',
            config,
          ),
        ),
    };
  }

  return templates;
}

export default class Select {
  selector: string;

  config: SelectConfig;

  elements: Choices[] = [];

  private abortController = new AbortController();

  constructor(selector = '.js-select', config: SelectConfig = {}) {
    this.selector = selector;

    const { language = detectLanguage(), ...rest } = config;
    this.config = { ...defaultConfig, ...texts[language], ...rest };

    this.elements = this._initialize();
  }

  private _onShowDropdown = (e: Event): void => {
    const container = (e.target as Element).parentElement?.parentElement;

    if (!container) return;

    if (container.classList.contains('is-flipped')) {
      container.classList.add('is-flipped-helper');
    }

    const searchInput = container.querySelector<HTMLInputElement>(
      '.choices__list--dropdown .choices__input',
    );

    if (searchInput) {
      // search input is not focused by default - perhaps due animation?
      // timeout fixes this issue
      setTimeout(() => {
        searchInput.focus();
      }, 100);
    }
  };

  private _onHideDropdown = (e: Event): void => {
    const dropdown = (e.target as Element).parentElement?.nextSibling;
    const container = (e.target as Element).parentElement?.parentElement;

    if (!container || !dropdown) return;

    const handleTransitionEnd = (evt: Event): void => {
      if ((evt as TransitionEvent).propertyName === 'visibility') {
        container.classList.remove('is-flipped');
        container.classList.remove('is-flipped-helper');

        dropdown.removeEventListener('transitionend', handleTransitionEnd);
      }
    };

    if (container.classList.contains('is-flipped-helper')) {
      container.classList.add('is-flipped');

      dropdown.addEventListener('transitionend', handleTransitionEnd);
    }
  };

  private _initialize(): Choices[] {
    const selects = [...document.querySelectorAll<HTMLSelectElement>(this.selector)];

    return selects.map((select, index) => {
      const config: ChoicesOptions = {
        ...this.config,
        classNames: { ...defaultConfig.classNames },
      };

      if (select.hasAttribute('data-is-initialized')) {
        return this.elements[index] as Choices;
      }

      if (
        (select.multiple && config.removeItemButton === undefined)
        || select.hasAttribute('data-clearable')
      ) {
        config.removeItemButton = true;
      }

      if (select.hasAttribute('placeholder')) {
        config.placeholderValue = select.getAttribute('placeholder');
      }

      if (select.hasAttribute('data-autocomplete')) {
        config.searchPlaceholderValue = select.getAttribute('placeholder');
        config.searchEnabled = true;
        config.searchChoices = true;
        config.shouldSort = true;
      }

      if (select.hasAttribute('data-select-position')) {
        config.position = select.getAttribute('data-select-position');
      }

      if (select.hasAttribute('data-select-small')) {
        // Array, not a space joined string: Choices feeds classNames straight
        // into classList.add(), which rejects tokens containing spaces - it
        // threw on every notice (loading / no choices / no results) render.
        // v1 had the same bug, it just never surfaced there (see porting.md).
        const item = classAttr(config.classNames.item);
        config.classNames.item = [item, `${item}--small`];
      }

      config.callbackOnCreateTemplates = (template: TemplateFn) =>
        getTemplates(template, select, config);

      const choice = new Choices(
        select,
        config as unknown as ConstructorParameters<typeof Choices>[1],
      );

      const containerOuter = (choice as unknown as {
        containerOuter: { element: HTMLElement };
        dropdown: { element: HTMLElement };
      });

      containerOuter.containerOuter.element.classList.add('select');

      // disable input if [disabled] or '.disabled'
      if (select.disabled || select.classList.contains('disabled')) {
        choice.disable();
      }

      const unwantedClasses = ['choices__input', 'is-hidden', 'js-select'];

      // add classes from select to choices
      [...select.classList]
        .filter(className => !unwantedClasses.includes(className))
        .forEach(className =>
          containerOuter.containerOuter.element.classList.add(className));

      if (config.searchEnabled) {
        const input = containerOuter.dropdown.element.querySelector('input');

        if (input) {
          input.classList.add('input', 'input--small', 'input--search');
        }
      }

      select.addEventListener('showDropdown', this._onShowDropdown, {
        signal: this.abortController.signal,
      });
      select.addEventListener('hideDropdown', this._onHideDropdown, {
        signal: this.abortController.signal,
      });

      select.setAttribute('data-is-initialized', '');

      return choice;
    });
  }

  update(): void {
    this.elements = this._initialize();
  }

  destroy(): void {
    this.abortController.abort();
    this.elements.forEach(choice => {
      // Clear the init guard so a later init picks the select up again.
      const passed = (choice as unknown as {
        passedElement?: { element?: Element };
      }).passedElement;
      passed?.element?.removeAttribute('data-is-initialized');

      choice.destroy();
    });
    this.elements = [];
  }
}
